import "@/App.css";
import { lazy, Suspense, ReactNode } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import Landing from "@/pages/Landing";
import CookieConsent from "@/components/CookieConsent";
import IosInstallPrompt from "@/components/IosInstallPrompt";
import TestModeBanner from "@/components/TestModeBanner";
import ErrorBoundary from "@/components/ErrorBoundary";

/*
  Code-splitting strategy:
  - Landing (the marketing root) ships in the initial bundle. Most cold-traffic
    visits hit `/`, so keeping it eager avoids a Suspense fallback flash on
    first paint.
  - Everything else lazy-loads on first navigation. This pulls the studio
    + 14 admin pages + billing + PayPal SDK provider + recharts out of the
    initial JS bundle. Big win on first-paint speed for cold visitors who
    never log in (e.g., search-engine crawlers, organic landing-page hits).
*/
const Login           = lazy(() => import("@/pages/Login"));
const Register        = lazy(() => import("@/pages/Register"));
const ForgotPassword  = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword   = lazy(() => import("@/pages/ResetPassword"));
const VerifyEmail     = lazy(() => import("@/pages/VerifyEmail"));
const Dashboard       = lazy(() => import("@/pages/Dashboard"));
const History         = lazy(() => import("@/pages/History"));
const Account         = lazy(() => import("@/pages/Account"));
const Billing         = lazy(() => import("@/pages/Billing"));
const AdminLayout     = lazy(() => import("@/pages/AdminLayout"));
const AdminDashboard  = lazy(() => import("@/pages/AdminDashboard"));
const PromptFramework = lazy(() => import("@/pages/PromptFramework"));
const Integrations    = lazy(() => import("@/pages/Integrations"));
const UserManagement  = lazy(() => import("@/pages/admin/UserManagement"));
const AdminFeedback   = lazy(() => import("@/pages/admin/AdminFeedback"));
const AdminCRM        = lazy(() => import("@/pages/admin/AdminCRM"));
const AdminCoupons    = lazy(() => import("@/pages/admin/AdminCoupons"));
const AdminAuditLog   = lazy(() => import("@/pages/admin/AdminAuditLog"));
const AdminPricing    = lazy(() => import("@/pages/admin/AdminPricing"));
const AdminStorage    = lazy(() => import("@/pages/admin/AdminStorage"));
const AdminSettings   = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminSystemSettings = lazy(() => import("@/pages/admin/AdminSystemSettings"));
const AdminMarketing  = lazy(() => import("@/pages/admin/AdminMarketing"));
const AdminHealth     = lazy(() => import("@/pages/admin/AdminHealth"));
const AdminEmergentSpend = lazy(() => import("@/pages/admin/AdminEmergentSpend"));
const AdminEval         = lazy(() => import("@/pages/admin/AdminEval"));
const AdminJobDetail    = lazy(() => import("@/pages/admin/AdminJobDetail"));
const SocialClips     = lazy(() => import("@/pages/SocialClips"));
const Pricing         = lazy(() => import("@/pages/Pricing"));
const Roadmap         = lazy(() => import("@/pages/Roadmap"));
const AdminRoadmap    = lazy(() => import("@/pages/admin/AdminRoadmap"));
const Terms           = lazy(() => import("@/pages/Terms"));
const Privacy         = lazy(() => import("@/pages/Privacy"));
const NotFound        = lazy(() => import("@/pages/NotFound"));

// AdminHealth is registered for completeness even though /admin/health now
// redirects to /admin/system; keep the import alive so the lazy chunk exists
// for any bookmarked deep-link tooling.
void AdminHealth;

interface GuardProps {
  children: ReactNode;
}

const Protected = ({ children }: GuardProps) => {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        data-testid="auth-loading"
        role="status"
        aria-live="polite"
        aria-label="Checking authentication"
      >
        <div className="w-6 h-6 rounded-full border-2 border-stone-300 border-t-stone-900 spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

// iter49 P3 #4: Sora 2 video clips are an OPERATOR-ONLY tool — used internally
// for marketing-content creation to drive Stage Studios sign-ups, NOT a
// user-facing feature. Non-admin users who hit /clips get bounced to /studio.
// The /admin route already has its own admin-only gate.
const AdminOnly = ({ children }: GuardProps) => {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        data-testid="auth-loading"
        role="status"
        aria-live="polite"
        aria-label="Checking authentication"
      >
        <div className="w-6 h-6 rounded-full border-2 border-stone-300 border-t-stone-900 spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/studio" replace />;
  return <>{children}</>;
};

const PublicOnly = ({ children }: GuardProps) => {
  const { user } = useAuth();
  if (user) return <Navigate to={user.is_admin ? "/admin" : "/studio"} replace />;
  return <>{children}</>;
};

/* Suspense fallback shown while a lazy route's JS chunk is fetching. Matches
   the Protected loader visually so the transition is invisible. */
const RouteFallback = () => (
  <div
    className="min-h-screen flex items-center justify-center"
    data-testid="route-fallback"
    role="status"
    aria-live="polite"
    aria-label="Loading page"
  >
    <div className="w-6 h-6 rounded-full border-2 border-stone-300 border-t-stone-900 spin" />
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <CurrencyProvider>
          <BrowserRouter>
            <TestModeBanner />
            <Toaster position="bottom-right" />
            <CookieConsent />
            <IosInstallPrompt />
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/roadmap" element={<Roadmap />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
              <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
              <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/studio" element={<Protected><Dashboard /></Protected>} />
              <Route path="/history" element={<Protected><History /></Protected>} />
              <Route path="/account" element={<Protected><Account /></Protected>} />
              <Route path="/clips" element={<AdminOnly><SocialClips /></AdminOnly>} />
              <Route path="/billing" element={<Protected><Billing /></Protected>} />
              <Route path="/buy" element={<Protected><Billing mode="buy" /></Protected>} />
              <Route path="/integrations" element={<Protected><Integrations /></Protected>} />

              <Route path="/admin" element={<Protected><AdminLayout /></Protected>}>
                <Route index element={<AdminDashboard />} />
                <Route path="users" element={<UserManagement />} />
                <Route path="feedback" element={<AdminFeedback />} />
                <Route path="roadmap" element={<AdminRoadmap />} />
                <Route path="prompts" element={<PromptFramework />} />
                <Route path="crm" element={<AdminCRM />} />
                <Route path="kendal" element={<AdminCRM />} />
                <Route path="coupons" element={<AdminCoupons />} />
                <Route path="audit" element={<AdminAuditLog />} />
                <Route path="pricing" element={<AdminPricing />} />
                <Route path="storage" element={<AdminStorage />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="system" element={<AdminSystemSettings />} />
                <Route path="marketing" element={<AdminMarketing />} />
                <Route path="social" element={<Navigate to="/admin/marketing" replace />} />
                <Route path="health" element={<Navigate to="/admin/system" replace />} />
                <Route path="emergent-spend" element={<AdminEmergentSpend />} />
                <Route path="eval" element={<AdminEval />} />
                <Route path="eval/new" element={<AdminEval />} />
                <Route path="eval/runs/:runId" element={<AdminEval />} />
                <Route path="jobs/:jobId" element={<AdminJobDetail />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </CurrencyProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
